import { useEffect, useState, useCallback } from "react";
import { PureTooltip } from "components/Tooltip";
import Spinner from "components/Spinner";
import { intervalToDuration } from "date-fns";

const SECOND = 1000;
const COOLDOWN_PERIOD = 20 * SECOND;

const formatElapsedTime = (ms: number): string => {
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({
    start: 0,
    end: ms,
  });

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 0) return `${seconds}s`;
  return "just now";
};

const RefreshIndicator = ({
  updatedAt,
  refresh,
  isRefreshing,
  hasError,
  cooldownKey = "refresh-cooldown",
}: {
  updatedAt?: Date;
  refresh?: () => void;
  isRefreshing?: boolean;
  hasError?: boolean;
  cooldownKey?: string;
}) => {
  const [now, setNow] = useState(() => new Date());

  const getCooldownEnd = (): Date | null => {
    try {
      const stored = localStorage.getItem(cooldownKey);
      if (stored) {
        const endTime = new Date(stored);
        if (endTime > now) return endTime;
        localStorage.removeItem(cooldownKey);
      }
    } catch {}
    return null;
  };

  const [cooldownEndTime, setCooldownEndTime] = useState<Date | null>(
    getCooldownEnd
  );

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), SECOND);
    return () => clearInterval(interval);
  }, []);

  const elapsedTime = updatedAt ? now.getTime() - updatedAt.getTime() : 0;
  const cooldownRemaining = cooldownEndTime
    ? Math.max(0, cooldownEndTime.getTime() - now.getTime())
    : 0;
  const isInCooldown = cooldownRemaining > 0;
  const cooldownSeconds = Math.ceil(cooldownRemaining / SECOND);

  const handleRefresh = useCallback(() => {
    if (isInCooldown) return;
    const endTime = new Date(Date.now() + COOLDOWN_PERIOD);
    setCooldownEndTime(endTime);
    try {
      localStorage.setItem(cooldownKey, endTime.toISOString());
    } catch {}
    refresh?.();
  }, [refresh, isInCooldown, cooldownKey]);

  useEffect(() => {
    if (cooldownEndTime && cooldownRemaining === 0) {
      setCooldownEndTime(null);
      try {
        localStorage.removeItem(cooldownKey);
      } catch {}
    }
  }, [cooldownEndTime, cooldownRemaining, cooldownKey]);

  if (hasError && !isRefreshing) {
    return (
      <div className="flex flex-row items-center text-sm text-neutral-500">
        <PureTooltip
          content={
            isInCooldown ? `Wait ${cooldownSeconds + 1}s` : "Click to refresh"
          }
        >
          <button
            className={`${
              isInCooldown
                ? "opacity-50 cursor-not-allowed"
                : "hover:underline cursor-pointer"
            } focus:outline-none`}
            onClick={handleRefresh}
            disabled={isInCooldown}
          >
            Retry {isInCooldown && `(${cooldownSeconds}s)`}
          </button>
        </PureTooltip>
      </div>
    );
  }

  if (isRefreshing) {
    return (
      <div className="flex flex-row items-center text-sm text-neutral-500">
        <div className="inline-flex gap-x-1 items-center">
          <span>Updating...</span>
          <Spinner size="XXS" theme="secondary" />
        </div>
      </div>
    );
  }

  if (!updatedAt) return null;

  return (
    <div className="flex items-end h-full">
      <PureTooltip
        content={isInCooldown ? `Wait ${cooldownSeconds}s` : "Click to refresh"}
      >
        <button
          className={`text-sm text-neutral-500 ${
            isInCooldown
              ? "opacity-50 cursor-not-allowed"
              : "hover:underline cursor-pointer"
          } focus:outline-none`}
          onClick={handleRefresh}
          disabled={isInCooldown}
        >
          Last updated{" "}
          {formatElapsedTime(elapsedTime) === "just now"
            ? ""
            : `${formatElapsedTime(elapsedTime)} ago`}
        </button>
      </PureTooltip>
    </div>
  );
};

export default RefreshIndicator;
